import { isDevBuild } from "@/config/env";
import { isRecord } from "@/lib/typeGuards";

/**
 * Where the pasted app token lives when the app runs in a plain browser tab
 * instead of the native shell (see src/dev/DevLoginPage.tsx).
 *
 * Not to be confused with the WebView's own tokens, which live in
 * src/api/tokenStore.ts: this is the app credential a developer pastes in, and
 * `session` mode spends it once to mint a real WebView session.
 *
 * `sessionStorage`, not a build variable: a `VITE_*` value is inlined into the
 * bundle and would ship with it, and changing it needs a dev-server restart.
 * This one is scoped to a single tab, disappears when the tab closes, and is
 * unreachable in a production build.
 */

/**
 * How the pasted token is used.
 *
 * `session` is what /dev/login writes: the token mints a code, the code buys a
 * WebView session, and requests carry that session — the same path the app
 * takes.
 *
 * `bearer` sends the pasted token itself on every request, skipping the
 * exchange entirely. Nothing in the UI sets it; the e2e suite seeds it directly
 * so a test does not depend on a mint round trip it is not testing.
 */
export type DevAuthMode = "bearer" | "session";

export interface DevAuth {
  token: string;
  mode: DevAuthMode;
}

const STORAGE_KEY = "sgc.devAuth";

function isDevAuth(value: unknown): value is DevAuth {
  return (
    isRecord(value) &&
    typeof value["token"] === "string" &&
    value["token"].length > 0 &&
    (value["mode"] === "bearer" || value["mode"] === "session")
  );
}

/** Returns null in a production build, and whenever nothing valid is stored. */
export function readDevAuth(): DevAuth | null {
  if (!isDevBuild) return null;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return isDevAuth(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeDevAuth(auth: DevAuth): void {
  if (!isDevBuild) return;
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
}

export function clearDevAuth(): void {
  if (!isDevBuild) return;
  window.sessionStorage.removeItem(STORAGE_KEY);
}
